import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

/**
 * Guard on `booking/new/:propertyId`.
 *
 * Only activates when the URL carries `?source=qr-code` — i.e. the user
 * arrived via a QR scan or a direct property link.  In all other cases
 * (manual navigation, deep links without the param, form resumes) the
 * existing booking-form flow is left completely untouched.
 *
 * When active, redirects to `/property-entry/:propertyId` which checks for a
 * candidate booking and, if none exists, falls back to
 * `/booking/new/:propertyId?skipVerify=1`.
 *
 * Bypass conditions (let the normal booking form through):
 *  - no `?source=qr-code` param           → not a QR/direct-link entry
 *  - `bookingFormData` in sessionStorage  → user is resuming a partially-filled form
 *  - `?skipVerify=1` query param          → user explicitly chose to fill manually
 */
export const propertyEntryRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
) => {
  const propertyId = route.paramMap.get('propertyId') ?? '';
  if (!propertyId) return true;

  // Only intercept QR / direct-link navigations
  if (route.queryParamMap.get('source') !== 'qr-code') return true;

  // Resuming a stored booking form — never intercept
  if (sessionStorage.getItem('bookingFormData')) return true;

  // User explicitly chose to skip the verification flow
  if (route.queryParamMap.has('skipVerify')) return true;

  // No pasamos queryParams — property-entry no los necesita.
  // El skip usa ?skipVerify=1 para que el guard no vuelva a interceptar.
  return inject(Router).createUrlTree(['/property-entry', propertyId]);
};
